export function isValidImageUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
}

export function getServerMediaFilename(url: string): string | null {
  try {
    const parsed = new URL(url);
    const galleryMatch = parsed.pathname.match(/\/api\/gallery\/files\/([^/]+)$/);
    if (galleryMatch) return decodeURIComponent(galleryMatch[1]);

    const mediaMatch = parsed.pathname.match(/\/api\/media\/files\/([^/]+)$/);
    return mediaMatch ? decodeURIComponent(mediaMatch[1]) : null;
  } catch {
    return null;
  }
}

export function isServerMediaUrl(url: string): boolean {
  return getServerMediaFilename(url) !== null;
}

export function countImageUrlInHtml(html: string, url: string): number {
  if (!html || !url) return 0;

  const escapedUrl = url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`<img[^>]+src=["']${escapedUrl}["']`, 'gi');
  return (html.match(pattern) || []).length;
}

function getApiOrigin(): string {
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
  return new URL(apiBase).origin;
}

function isRelativeMediaPath(value: string): boolean {
  return value.startsWith('/api/media/files/') || value.startsWith('/api/gallery/files/');
}

/** True when imageUrl is a usable http(s) or API media path (not arbitrary text). */
export function isDisplayableImageUrl(url?: string | null): boolean {
  if (!url?.trim()) return false;
  const trimmed = url.trim();
  return isRelativeMediaPath(trimmed) || isValidImageUrl(trimmed);
}

/** Resolve imageUrl for <img src>; returns undefined for invalid values like plain text. */
export function getDisplayableImageUrl(url?: string | null): string | undefined {
  if (!url?.trim()) return undefined;
  const trimmed = url.trim();

  if (isRelativeMediaPath(trimmed)) {
    return `${getApiOrigin()}${trimmed}`;
  }

  if (!isValidImageUrl(trimmed)) {
    return undefined;
  }

  return resolveMediaUrl(trimmed);
}

/** Resolve media file URLs to the API host in local dev (fixes legacy localhost:3000/api/... links). */
export function resolveMediaUrl(url: string): string {
  if (!url) return url;

  try {
    const parsed = new URL(url);
    const apiOrigin = getApiOrigin();

    if (parsed.pathname.startsWith('/api/gallery/files/') || parsed.pathname.startsWith('/api/media/files/')) {
      return `${apiOrigin}${parsed.pathname}`;
    }

    if (
      (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1')
      && parsed.pathname.includes('/api/')
    ) {
      return `${apiOrigin}${parsed.pathname}`;
    }
  } catch {
    return url;
  }

  return url;
}

/** Rewrite img src attributes in article HTML for production API hosts. */
export function resolveMediaUrlsInHtml(html: string): string {
  if (!html) return html;

  return html.replace(/<img\b([^>]*?)>/gi, (tag, attrs: string) => {
    const srcMatch = attrs.match(/\ssrc=["']([^"']*)["']/i);
    if (!srcMatch || !srcMatch[1].trim()) {
      return '';
    }

    const displayable = getDisplayableImageUrl(srcMatch[1].trim());
    if (!displayable) {
      return '';
    }

    const withoutSrc = attrs.replace(/\ssrc=["'][^"']*["']/i, '');
    return `<img${withoutSrc} src="${displayable}">`;
  });
}
